import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  FormControl,
  FormLabel,
  FormHelperText,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormGroup,
  Checkbox,
  Grid,
  InputAdornment
} from '@mui/material';
import sharedStyles from '../../../../../IndeedIntegration/AdEditor/useStyles';
import usestyles from '../useStyles';
import ErrorIcon from '@mui/icons-material/Error';
import AddressInput from '../../../../../NewUI/Components/CustomUIElements/AddressInput';
import { parseSalary } from '../../salaryHelper';
import { stepPropTypes } from '../../../../../IndeedIntegration/AdEditor/Steps/types';

function Step1({ answers, setAnswers, jobAdParams, requiredRefs, activeError }) {
  const [value, setValue] = React.useState({
    description: [answers.address].filter((cur) => cur).join(', ')
  });
  const [titleTooLong, setTitleTooLong] = useState(false);
  const sharedClasses = sharedStyles();
  const classes = usestyles();

  // Check if title is too long when component mounts or answers.title changes
  useEffect(() => {
    setTitleTooLong(answers.title.length > 52);
  }, [answers.title]);

  function handleMultiCheck(value, state, key) {
    state.includes(value)
      ? setAnswers({ ...answers, [key]: state.filter((id) => id !== value) })
      : setAnswers({ ...answers, [key]: [...answers[key], value] });
  }

  return (
    <div className={sharedClasses.stepContainer}>
      <FormControl
        ref={requiredRefs.title}
        fullWidth={true}
        className={`${sharedClasses.formControl} ${sharedClasses.medium}`}
      >
        <FormLabel required={true} className={sharedClasses.label}>
          Job Title
        </FormLabel>
        <TextField
          error={activeError === requiredRefs.title || titleTooLong}
          className={sharedClasses.textField}
          variant="outlined"
          value={answers.title}
          onChange={(e) => {
            const newValue = e.target.value;
            if (newValue.length <= 52 && newValue !== answers.title) {
              setAnswers({ ...answers, title: newValue });
            }
          }}
          InputProps={{
            endAdornment: (activeError === requiredRefs.title || titleTooLong) && (
              <div className={sharedClasses.error}>
                <ErrorIcon color="error" />
                <span>
                  {titleTooLong 
                    ? "Job title cannot exceed 52 characters" 
                    : "This field is required"}
                </span>
              </div>
            )
          }}
        />
        <FormHelperText className={sharedClasses.helperText}>
          {answers.title.length}/52
        </FormHelperText>
      </FormControl>

      <AddressInput
        requiredRef={requiredRefs.address}
        classes={sharedClasses}
        value={value}
        setValue={setValue}
        activeError={activeError === requiredRefs.address}
        answers={answers}
        setAnswers={setAnswers}
        viewJobsAd={true}
      />

      <FormControl
        ref={requiredRefs.classification}
        fullWidth={true}
        className={`${sharedClasses.formControl} ${sharedClasses.medium}`}
      >
        <FormLabel required={true} className={sharedClasses.label}>
          Classification
        </FormLabel>
        <Select
          classes={{ select: sharedClasses.input }}
          variant="outlined"
          value={answers.classification}
          onChange={(e) => setAnswers({ ...answers, classification: e.target.value })}
          displayEmpty={true}
          renderValue={(value) =>
            value || <span className={sharedClasses.placeholder}>Select an option</span>
          }
          endAdornment={
            activeError === requiredRefs.classification && (
              <div className={sharedClasses.error}>
                <ErrorIcon color="error" />
                <span>This field is required</span>
              </div>
            )
          }
        >
          {jobAdParams.classifications.map((classification) => (
            <MenuItem key={classification[1]} value={classification[0]}>
              {classification[0]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl
        ref={requiredRefs.workType}
        fullWidth={true}
        className={`${sharedClasses.formControl}`}
      >
        <FormLabel required={true} className={sharedClasses.label}>
          Work type
          {activeError === requiredRefs.workType && (
            <div className={classes.error}>
              <ErrorIcon color="error" />
              <span>This field is required</span>
            </div>
          )}
        </FormLabel>
        <RadioGroup
          value={answers.workType}
          onChange={(e) => setAnswers({ ...answers, workType: e.target.value })}
        >
          {jobAdParams.worktypes.map((type) => (
            <FormControlLabel
              key={type[1]}
              label={type[0]}
              value={type[0]}
              control={<Radio color="secondary" />}
            />
          ))}
        </RadioGroup>
      </FormControl>

      <FormControl
        ref={requiredRefs.workLocation}
        fullWidth={true}
        className={`${sharedClasses.formControl}`}
      >
        <FormLabel required={true} className={sharedClasses.label}>
          Work location
          {activeError === requiredRefs.workLocation && (
            <div className={classes.error}>
              <ErrorIcon color="error" />
              <span>This field is required</span>
            </div>
          )}
        </FormLabel>
        <RadioGroup
          value={answers.workLocation}
          onChange={(e) => setAnswers({ ...answers, workLocation: e.target.value })}
        >
          {jobAdParams.worklocations.map((location) => (
            <FormControlLabel
              key={location[1]}
              label={location[0]}
              value={location[0]}
              control={<Radio color="secondary" />}
            />
          ))}
        </RadioGroup>
      </FormControl>

      <FormControl
        ref={requiredRefs.payType}
        fullWidth={true}
        className={`${sharedClasses.formControl}`}
      >
        <FormLabel required={true} className={sharedClasses.label}>
          Pay details
          {activeError === requiredRefs.payType && (
            <div className={classes.error}>
              <ErrorIcon color="error" />
              <span>This field is required</span>
            </div>
          )}
        </FormLabel>
        <RadioGroup
          value={answers.payType}
          onChange={(e) => setAnswers({ ...answers, payType: e.target.value })}
        >
          {jobAdParams.paytypes.map((type) => (
            <FormControlLabel
              key={type[1]}
              label={type[0]}
              value={type[0]}
              control={<Radio color="secondary" />}
            />
          ))}
        </RadioGroup>
      </FormControl>

      <FormControl
        ref={requiredRefs.salary}
        fullWidth={true}
        className={`${sharedClasses.formControl} ${sharedClasses.medium}`}
      >
        <FormLabel focused={false} className={sharedClasses.label}>
          Pay Range
          <span className={sharedClasses.asterisk}> *</span>
          {activeError === requiredRefs.salary && (
            <div className={sharedClasses.error}>
              <ErrorIcon color="error" />
              <span>This field is required</span>
            </div>
          )}
        </FormLabel>
        <span className={sharedClasses.subLabel}>Enter a pay range to offer candidates</span>
        <Grid container classes={{ root: sharedClasses.controlledNumberInput }}>
          <Grid
            item
            container
            alignContent="center"
            alignItems="center"
            justifyContent="center"
            wrap="nowrap"
          >
            <React.Fragment>
              <Box m={1} ml={0}>
                <TextField
                  error={activeError === requiredRefs.salary}
                  className={sharedClasses.textField}
                  variant="outlined"
                  onBlur={() => {
                    if (answers.payRangeLow > answers.payRangeHigh)
                      setAnswers({ ...answers, payRangeHigh: answers.payRangeLow });
                  }}
                  value={Number(answers.payRangeLow).toLocaleString()}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">From</InputAdornment>
                  }}
                  onChange={(e) =>
                    setAnswers({ ...answers, payRangeLow: parseSalary(e.target.value) })
                  }
                />
              </Box>
              <Box m={1}>
                <TextField
                  error={activeError === requiredRefs.salary}
                  className={sharedClasses.textField}
                  variant="outlined"
                  value={Number(answers.payRangeHigh).toLocaleString()}
                  onBlur={() => {
                    if (answers.payRangeHigh < answers.payRangeLow)
                      setAnswers({ ...answers, payRangeHigh: answers.payRangeLow });
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">To</InputAdornment>
                  }}
                  onChange={(e) =>
                    setAnswers({ ...answers, payRangeHigh: parseSalary(e.target.value) })
                  }
                />
              </Box>
              <span className={classes.salaryIntervalLabel}>
                per {answers.payType === 'Annual salary' ? 'Year' : 'Hour'}
              </span>
            </React.Fragment>
          </Grid>
        </Grid>
      </FormControl>

      <FormControl
        fullWidth={true}
        className={`${sharedClasses.formControl} ${sharedClasses.medium}`}
      >
        <FormLabel focused={false} className={sharedClasses.label}>
          Benefits
        </FormLabel>
        <FormGroup>
          <Grid container>
            {jobAdParams.extrabenefits.map((benefit) => (
              <Grid key={benefit[1]} item xs={6}>
                <FormControlLabel
                  label={benefit[0]}
                  value={benefit[0]}
                  control={
                    <Checkbox
                      checked={answers?.benefits?.includes(benefit[0])}
                      onChange={(e) =>
                        handleMultiCheck(e.target.value, answers.benefits, 'benefits')
                      }
                    />
                  }
                />
              </Grid>
            ))}
          </Grid>
        </FormGroup>
      </FormControl>
    </div>
  );
}

Step1.propTypes = stepPropTypes;

export default Step1;
